import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from './components/Navbar';
import { AccessibilityBanner } from './components/AccessibilityBanner';
import { Homepage } from './components/Homepage';
import { JourneySection } from './components/JourneySection';
import { DentonsSection } from './components/DentonsSection';
import { GibbsSection } from './components/GibbsSection';
import { GilesSection } from './components/GilesSection';
import { RobertsSection } from './components/RobertsSection';
import { AcknowledgementSection } from './components/AcknowledgementSection';
import { SearchResults } from './components/SearchResults';
import { MemberDossierModal } from './components/MemberDossierModal';
import { UploadModal } from './components/UploadModal';
import { FirebaseExportModal } from './components/FirebaseExportModal';
import { VoiceReaderProvider, useVoiceReader } from './context/VoiceReaderContext';
import { AuthProvider } from './context/AuthContext';
import { VoiceReaderBar } from './components/VoiceReaderBar';

import { INITIAL_MEMBERS, INITIAL_STORIES, INITIAL_ARCHIVE } from './data/initialData';
import { 
  FamilyMember, 
  Story, 
  ArchiveItem, 
  AccessibilitySettings, 
  ArchiveTab, 
  TAB_TO_PATH, 
  getTabFromPath 
} from './types';
import { safeLocalStorage } from './lib/storage';

function HighlightTracker() {
  const { activeElementId } = useVoiceReader();
  
  useEffect(() => {
    // Remove active highlight from all elements
    document.querySelectorAll('.narrating-active').forEach(el => {
      el.classList.remove('narrating-active');
    });

    // Add active highlight to currently narrated element
    if (activeElementId) {
      const el = document.getElementById(activeElementId);
      if (el) {
        el.classList.add('narrating-active');
      }
    }
  }, [activeElementId]);

  return null;
}

function AppContent() {
  // Initialize Active Tab from current window path or default to 'homepage'
  const [activeTab, setActiveTab] = useState<ArchiveTab>(() => {
    if (typeof window !== 'undefined') {
      const initialPath = window.location.pathname;
      return getTabFromPath(initialPath);
    }
    return 'homepage';
  });

  const [searchQuery, setSearchQuery] = useState<string>('');

  // Main Data States with LocalStorage Persistence
  const [members, setMembers] = useState<FamilyMember[]>(() => {
    const saved = safeLocalStorage.getItem('family_members_v3');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // If storage contains old mock test IDs like m1, invalidate and use INITIAL_MEMBERS
        if (Array.isArray(parsed) && parsed.some(m => m.id === 'm1' || m.birthPlace?.includes('Hartford'))) {
          return INITIAL_MEMBERS;
        }
        return parsed;
      } catch (e) {
        console.warn('Failed to parse members from storage:', e);
      }
    }
    return INITIAL_MEMBERS;
  });

  const [stories, setStories] = useState<Story[]>(() => {
    const saved = safeLocalStorage.getItem('family_stories_v3');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.some(s => s.taggedMemberIds?.includes('m1'))) {
          return INITIAL_STORIES;
        }
        return parsed;
      } catch (e) {
        console.warn('Failed to parse stories from storage:', e);
      }
    }
    return INITIAL_STORIES;
  });

  const [archiveItems, setArchiveItems] = useState<ArchiveItem[]>(() => {
    const saved = safeLocalStorage.getItem('family_archive_v3');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.some(a => a.taggedMemberIds?.includes('m1'))) {
          return INITIAL_ARCHIVE;
        }
        return parsed;
      } catch (e) {
        console.warn('Failed to parse archive items from storage:', e);
      }
    }
    return INITIAL_ARCHIVE;
  });

  // Modal States
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showFirebaseModal, setShowFirebaseModal] = useState<boolean>(false);

  // Accessibility State
  const [accessibility, setAccessibility] = useState<AccessibilitySettings>({
    fontSize: 'normal',
    colorTheme: 'warm',
    textToSpeechEnabled: false,
    screenReaderFriendly: true
  });

  // Save changes to localStorage
  useEffect(() => {
    safeLocalStorage.setItem('family_members_v3', JSON.stringify(members));
  }, [members]);

  useEffect(() => {
    safeLocalStorage.setItem('family_stories_v3', JSON.stringify(stories));
  }, [stories]);

  useEffect(() => {
    safeLocalStorage.setItem('family_archive_v3', JSON.stringify(archiveItems));
  }, [archiveItems]);

  // Handle URL changes & Browser Back/Forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname;
      const tab = getTabFromPath(currentPath);
      setActiveTab(tab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Handler for programmatic tab navigation with clean URL synchronization
  const handleTabChange = useCallback((newTab: ArchiveTab) => {
    setActiveTab(newTab);
    const targetPath = TAB_TO_PATH[newTab] || '/';
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Selected Member for Dossier
  const activeMember = members.find(m => m.id === selectedMemberId);

  return (
    <div className="min-h-screen bg-[#fbf9f4] text-[#2c221e] flex flex-col font-sans selection:bg-[#e8dec9] selection:text-[#2c221e]">
      
      {/* Top Banner & Accessibility Strip */}
      <AccessibilityBanner />

      {/* Navbar with Clean Hierarchical Tabs & Dropdown for The Journey */}
      <Navbar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        accessibility={accessibility}
        setAccessibility={setAccessibility}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      {/* Main Page Layout */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-8">
        
        {/* Search Mode Active */}
        {searchQuery.trim() !== '' ? (
          <SearchResults
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            members={members}
            stories={stories}
            archiveItems={archiveItems}
            onSelectMember={id => setSelectedMemberId(id)}
            onSelectStory={() => handleTabChange('homepage')}
            onSelectArchiveItem={() => handleTabChange('homepage')}
            setActiveTab={tab => handleTabChange(tab as ArchiveTab)}
          />
        ) : (
          <>
            {/* 1. Homepage */}
            {activeTab === 'homepage' && (
              <Homepage onNavigate={handleTabChange} />
            )}

            {/* 2. The Journey (Parent Hub & Sub-pages) */}
            {(activeTab === 'journey' || 
              activeTab === 'journey-suffolk' || 
              activeTab === 'journey-essentials' || 
              activeTab === 'journey-why-they-came') && (
              <JourneySection 
                currentTab={activeTab} 
                onNavigate={handleTabChange} 
              />
            )}

            {/* 3. The Dentons */}
            {activeTab === 'dentons' && <DentonsSection />}

            {/* 4. The Gibb Family */}
            {activeTab === 'gibbs' && <GibbsSection />}

            {/* 5. The Giles Family */}
            {activeTab === 'giles' && <GilesSection />}

            {/* 6. The Roberts Family */}
            {activeTab === 'roberts' && <RobertsSection />}

            {/* 7. Acknowledgement and Thanks */}
            {activeTab === 'acknowledgement' && <AcknowledgementSection />}
          </>
        )}

      </main>

      {/* Member Dossier Modal */}
      {activeMember && (
        <MemberDossierModal
          member={activeMember}
          person={activeMember}
          allMembers={members}
          allStories={stories}
          allArchiveItems={archiveItems}
          onClose={() => setSelectedMemberId(null)}
          onSelectMember={id => setSelectedMemberId(id)}
          onSelectStory={() => {
            handleTabChange('homepage');
            setSelectedMemberId(null);
          }}
          onSelectTab={tab => {
            handleTabChange(tab);
            setSelectedMemberId(null);
          }}
          textToSpeechEnabled={accessibility.textToSpeechEnabled}
        />
      )}

      {/* AI Assisted Upload Modal */}
      {showUploadModal && (
        <UploadModal
          allMembers={members}
          onAddStory={newStory => setStories(prev => [newStory, ...prev])}
          onAddArchiveItem={newItem => setArchiveItems(prev => [newItem, ...prev])}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {/* Firebase Export Modal */}
      {showFirebaseModal && (
        <FirebaseExportModal
          members={members}
          stories={stories}
          archiveItems={archiveItems}
          onClose={() => setShowFirebaseModal(false)}
        />
      )}

      {/* Footer */}
      <footer className="no-print bg-[#2c221e] text-amber-100/80 py-8 px-4 mt-12 border-t-4 border-[#7a5b3a]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs md:text-sm">
          <div className="space-y-1 text-center sm:text-left font-serif">
            <p className="font-bold text-base text-amber-100">
              Stories from the First Family Members in New Zealand
            </p>
            <p className="text-amber-200/60 text-xs">
              Christine Negus Archival Compilation (December 2021) & voice reader •  Made possible by <a href="https://chapmansolutions.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-100 transition">https://chapmansolutions.org</a>
            </p>
          </div>

          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-3.5 py-2 bg-[#4a3525] hover:bg-[#634833] text-amber-100 rounded-xl font-serif font-bold transition shadow-xs border border-amber-200/20"
          >
            ↑ Back to Top
          </button>
        </div>
      </footer>

      {/* Persistent Archival Voice Reader Audio Bar & Highlight Tracking */}
      <HighlightTracker />
      <VoiceReaderBar />

    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <VoiceReaderProvider>
        <AppContent />
      </VoiceReaderProvider>
    </AuthProvider>
  );
}
